import { assert } from "@/helpers/console.ts";
import { type FC, type PointerEvent, useRef, useState } from "react";
import { styled } from "@linaria/react";
import { FloatingImageWindow } from "@/components/chat/FloatingImageWindow.tsx";
import { RenderLatex } from "@/helpers/latex.tsx";

const DRAG_THRESHOLD = 8;

const Image = styled.img`
  width: 100%;
  max-width: 35rem;
  display: block;
  cursor: grab;
  border-radius: 4px;
  user-select: none;
  min-width: 35rem;
  min-height: 26.25rem;
  object-fit: contain;

  &:active {
    cursor: grabbing;
  }
`;

const Placeholder = styled.div`
  width: min(28rem, 100%);
  aspect-ratio: 4/3;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  color: #64748b;
  font-size: 0.875rem;
  padding: 1rem;
  text-align: center;
`;

interface FlowMessageImageProps {
  src: string;
  alt: string;
  message: string;
}

interface Size {
  width: number;
  height: number;
}

export const FlowMessageImage: FC<FlowMessageImageProps> = ({ src, alt, message }) => {
  assert(src.length !== 0, "Image src needs to be more than 0");
  assert(alt.length !== 0, "Image alt needs to be more than 0");

  const imgRef = useRef<HTMLImageElement>(null);
  const [imgSize, setImgSize] = useState<Size>({ width: 100, height: 100 });

  const [floating, setFloating] = useState<{ x: number; y: number } | null>(null);
  const startPos = useRef({ x: 0, y: 0 });

  const onPointerDown = (e: PointerEvent<HTMLImageElement>) => {
    e.preventDefault();
    startPos.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLImageElement>) => {
    if (e.buttons !== 1) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      e.currentTarget.releasePointerCapture(e.pointerId);
      setFloating({ x: e.clientX, y: e.clientY });
    }
  };

  if (floating) {
    return (
      <>
        {message && message != "" && <RenderLatex message={message} />}
        <Placeholder style={{ width: imgSize.width, height: imgSize.height }}>
          Image open in window — close it to return it here
        </Placeholder>
        <FloatingImageWindow
          src={src}
          alt={alt}
          initialX={floating.x}
          initialY={floating.y}
          onClose={() => setFloating(null)}
        />
      </>
    );
  }

  return (
    <>
      {message && message != "" && <RenderLatex message={message} />}
      <Image
        src={src}
        alt={alt}
        ref={imgRef}
        onLoad={() => {
          if (imgRef.current) {
            setImgSize({
              width: imgRef.current.width,
              height: imgRef.current.height,
            });
          }
        }}
        draggable={false}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        loading={"lazy"}
      />
    </>
  );
};
